const TEAMS_AUTH_STATE_KEY = 'slick:teams-ms-entra-state';
const TEAMS_AUTH_PROOFS_KEY = 'slick:teams-ms-entra-proofs';
const WEB_AUTH_KEY = 'slick:ms-entra-web-auth';
const PENDING_EXCHANGE_KEY = 'slick:ms-entra-pending-exchange';
const WEB_STATE_PREFIX = 'web.';
const TEAMS_AUTH_STATE_LOG_PREFIX = '[TeamsMSEntraState]';

/**
 * Proof that this browser started the Microsoft sign-in (SRC-11952). The API sends the PKCE
 * `code_verifier` when redeeming the code and checks the ID token carries the `nonce`, so a code
 * from somebody else's sign-in can't be replayed here to log this browser in as them.
 */
export interface MSEntraProofs {
  code_verifier: string;
  nonce: string;
}

/** What goes on the authorize URL. */
export interface MSEntraAuthorizeParams {
  code_challenge: string;
  nonce: string;
}

export function createTeamsAuthState(): string {
  const state = createRandomState();
  sessionStorage.setItem(TEAMS_AUTH_STATE_KEY, state);
  console.info(`${TEAMS_AUTH_STATE_LOG_PREFIX} Created and stored Teams auth state`);
  return state;
}

export function validateTeamsAuthState(state: string | null | undefined): boolean {
  if (!state) {
    return false;
  }

  const storedState = sessionStorage.getItem(TEAMS_AUTH_STATE_KEY);
  sessionStorage.removeItem(TEAMS_AUTH_STATE_KEY);
  console.info(`${TEAMS_AUTH_STATE_LOG_PREFIX} Validated Teams auth state`, { isValid: storedState === state });

  return storedState === state;
}

/** Web redirect flow: returns the authorize params and state, keeping the proofs for the callback. */
export async function startWebAuth(): Promise<MSEntraAuthorizeParams & { state: string }> {
  const { proofs, params } = await createProofs();
  const state = WEB_STATE_PREFIX + createRandomState();
  sessionStorage.setItem(WEB_AUTH_KEY, JSON.stringify({ state, ...proofs }));
  return { ...params, state };
}

export function isWebAuthState(state: string | null | undefined): boolean {
  return !!state && state.startsWith(WEB_STATE_PREFIX);
}

/** The proofs for a web callback, or null when this tab didn't start a sign-in with that state. */
export function consumeWebAuth(state: string): MSEntraProofs | null {
  const stored = readAndRemove<MSEntraProofs & { state: string }>(WEB_AUTH_KEY);
  if (!stored || stored.state !== state) {
    return null;
  }
  return { code_verifier: stored.code_verifier, nonce: stored.nonce };
}

/** Teams flow, in the popup: the popup redirects to Microsoft, then hands the proofs back with the code. */
export async function startTeamsAuthProofs(): Promise<MSEntraAuthorizeParams> {
  const { proofs, params } = await createProofs();
  sessionStorage.setItem(TEAMS_AUTH_PROOFS_KEY, JSON.stringify(proofs));
  return params;
}

export function consumeTeamsAuthProofs(): MSEntraProofs | null {
  return readAndRemove<MSEntraProofs>(TEAMS_AUTH_PROOFS_KEY);
}

/** Teams flow, in the main tab: keeps the proofs the popup returned for the /ms-entra exchange. */
export function storePendingExchange(proofs: MSEntraProofs): void {
  sessionStorage.setItem(PENDING_EXCHANGE_KEY, JSON.stringify(proofs));
}

export function consumePendingExchange(): MSEntraProofs | null {
  return readAndRemove<MSEntraProofs>(PENDING_EXCHANGE_KEY);
}

async function createProofs(): Promise<{ proofs: MSEntraProofs; params: MSEntraAuthorizeParams }> {
  const code_verifier = base64Url(randomBytes(32));
  const nonce = createRandomState();
  const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(code_verifier));
  return {
    proofs: { code_verifier, nonce },
    params: { code_challenge: base64Url(new Uint8Array(digest)), nonce },
  };
}

function readAndRemove<T>(key: string): T | null {
  const raw = sessionStorage.getItem(key);
  sessionStorage.removeItem(key);
  try {
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  crypto.getRandomValues(bytes);
  return bytes;
}

function base64Url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

function createRandomState(): string {
  return Array.from(randomBytes(16), (byte) => byte.toString(16).padStart(2, '0')).join('');
}
